import { User } from '@/app/types/User';
import { Team } from '@/components/teams/types';
import { TeamPreferences } from '@/services/teams.service';
import { DealScoreEventPreferences } from '@/app/types/DealScoreEventPreferences';

export interface OnboardingSkipCheckData {
  userDetails: User | null;
  team: Team | null;
  teamPreferences: TeamPreferences | null | undefined;
  effectiveProducts: string[];
  effectiveBuyingPersona: string[];
}

/**
 * Calculates effective preferences (team preferences take precedence over user preferences)
 * @param userDetails - User details
 * @param teamPreferences - Team preferences
 * @returns Object with effective products and buying personas and deal-score events
 */
export function calculateEffectivePreferences(
  userDetails: User | null,
  teamPreferences: TeamPreferences | null | undefined,
) {
  const effectiveProducts =
    teamPreferences?.products && teamPreferences.products.length > 0
      ? teamPreferences.products
      : userDetails?.products || [];

  const effectiveBuyingPersona =
    teamPreferences?.buyingPersona && teamPreferences.buyingPersona.length > 0
      ? teamPreferences.buyingPersona
      : userDetails?.buyingPersona || [];

  // Team prefs take precedence only when they actually carry a non-empty
  // `selected` array — an unset team value should fall through to the user's
  // own preferences, matching `getUserEffectiveDealScoreEvents` on the backend.
  const teamDealScoreEvents = teamPreferences?.dealScoreEventPreferences;
  const userDealScoreEvents = userDetails?.dealScoreEventPreferences;
  const effectiveDealScoreEventPreferences: DealScoreEventPreferences | null =
    teamDealScoreEvents &&
    Array.isArray(teamDealScoreEvents.selected) &&
    teamDealScoreEvents.selected.length > 0
      ? teamDealScoreEvents
      : userDealScoreEvents &&
        Array.isArray(userDealScoreEvents.selected) &&
        userDealScoreEvents.selected.length > 0
      ? userDealScoreEvents
      : null;

  console.log('effectiveProducts', effectiveProducts);
  console.log('effectiveBuyingPersona', effectiveBuyingPersona);

  return {
    effectiveProducts,
    effectiveBuyingPersona,
    effectiveDealScoreEventPreferences,
  };
}

/**
 * Determines if onboarding should be skipped based on user and team preferences
 * @param data - The user and team data to check
 * @returns true if onboarding should be skipped, false otherwise
 */
export function shouldSkipOnboarding(data: OnboardingSkipCheckData): boolean {
  const {
    userDetails,
    team,
    teamPreferences,
    effectiveProducts,
    effectiveBuyingPersona,
  } = data;

  console.log('[shouldSkipOnboarding] Checking onboarding skip logic:', {
    hasUserDetails: !!userDetails,
    hasTeam: !!team,
    teamId: team?.id,
    hasTeamPreferences: !!teamPreferences,
    effectiveProducts,
    effectiveBuyingPersona,
    isSkipped: userDetails?.onBoardingState?.isSkipped,
  });

  if (!userDetails) return false;

  // 1. User has explicitly skipped onboarding
  if (userDetails.onBoardingState?.isSkipped) {
    console.log(
      '[shouldSkipOnboarding] Skipping onboarding - user explicitly skipped',
    );
    return true;
  }

  if (team?.organizationId) {
    return true;
  }

  // 2. Non-owner team member - skip onboarding (inherits team owner's configuration)
  if (team && String(team.owner?.id) !== String(userDetails.id)) {
    console.log(
      '[shouldSkipOnboarding] Skipping onboarding - user is non-owner team member:',
      team.id,
    );
    return true;
  }
  // Team owners fall through to check effective preferences below

  // 3. User has effective products and buying personas (either from team or user)
  const hasEffectivePreferences =
    effectiveProducts.length > 0 && effectiveBuyingPersona.length > 0;

  if (hasEffectivePreferences) {
    console.log(
      '[shouldSkipOnboarding] Skipping onboarding - user has effective preferences',
    );
    return true;
  }

  console.log(
    '[shouldSkipOnboarding] Onboarding NOT skipped - user needs onboarding',
  );
  return false;
}

/**
 * Determines if onboarding is needed based on user and team preferences
 * @param data - The user and team data to check
 * @returns true if onboarding is needed, false otherwise
 */
export function needsOnboarding(data: OnboardingSkipCheckData): boolean {
  const { userDetails } = data;

  console.log('[needsOnboarding] Checking if onboarding is needed:', {
    hasUserDetails: !!userDetails,
    onboardingState: userDetails?.onBoardingState,
  });

  if (!userDetails) {
    console.log('[needsOnboarding] No user details - onboarding needed');
    return true;
  }

  // If we should skip onboarding, then onboarding is not needed
  if (shouldSkipOnboarding(data)) {
    console.log('[needsOnboarding] Onboarding should be skipped - not needed');
    return false;
  }

  // Check traditional onboarding completion
  const onboarding = userDetails.onBoardingState;
  const needsOnboarding =
    !onboarding ||
    !onboarding.departments?.isDone ||
    !onboarding.product?.isDone;

  console.log('[needsOnboarding] Traditional onboarding check:', {
    hasOnboarding: !!onboarding,
    departmentsDone: onboarding?.departments?.isDone,
    productDone: onboarding?.product?.isDone,
    needsOnboarding,
  });

  return needsOnboarding;
}

/**
 * Determines if user is considered onboarded based on user and team preferences
 * @param data - The user and team data to check
 * @returns true if user is onboarded, false otherwise
 */
export function isOnboarded(data: OnboardingSkipCheckData): boolean {
  const { userDetails } = data;

  if (!userDetails) return false;

  // If we should skip onboarding, consider user onboarded
  if (shouldSkipOnboarding(data)) {
    return true;
  }

  if (!userDetails.onBoardingState) return false;

  // Otherwise, check traditional onboarding completion
  const onboarding = userDetails.onBoardingState;
  return !!(
    onboarding.followingCompanies?.isDone &&
    onboarding.departments?.isDone &&
    onboarding.product?.isDone &&
    (onboarding.freeReport?.isPurchased || onboarding.freeReport?.shouldSkip)
  );
}
